import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Feather, Flower2, Landmark, SearchCheck, Headphones, Newspaper, GraduationCap, Search, Quote, ArrowLeft } from "lucide-react";
import { api, HERO_BG, ABOUT_IMG, SectionCard, Flourish } from "@/components/Common";

export const SECTIONS = [
  { to: "/urdu-adab", icon: BookOpen, title: "اردو ادب", desc: "اردو شاعری، نثر، تنقید، افسانہ اور دیگر اصناف کا عظیم ذخیرہ", testid: "section-card-urdu-adab", accent: "#8f2d1f", tint: "#f3d9d2" },
  { to: "/authors", icon: Feather, title: "شعراء و ادباء", desc: "اردو کے نامور شعراء و ادباء کی حیات اور خدمات", testid: "section-card-authors", accent: "#1f5c4d", tint: "#d5ece3" },
  { to: "/genres", icon: Flower2, title: "اصنافِ ادب", desc: "شعر و شاعری، نثر، ڈرامہ، افسانہ اور تمام ادبی اصناف", testid: "section-card-genres", accent: "#9c6a10", tint: "#f4e6c8" },
  { to: "/history", icon: Landmark, title: "تاریخِ ادب", desc: "اردو ادب کی تاریخی ارتقا اور اہم ادوار", testid: "section-card-history", accent: "#274156", tint: "#d8e3ee" },
  { to: "/articles", icon: SearchCheck, title: "تنقید و تحقیق", desc: "تنقیدی مضامین، تحقیقی مقالے اور علمی مباحث", testid: "section-card-articles", accent: "#5b3a8c", tint: "#e6ddf3" },
  { to: "/media", icon: Headphones, title: "اردو میڈیا", desc: "YouTube، Podcasts، Spotify اور دیگر آڈیو و ویڈیو مواد", testid: "section-card-media", accent: "#a03d63", tint: "#f4dae4" },
  { to: "/journals", icon: Newspaper, title: "اردو رسائل و جرائد", desc: "قدیم و جدید اردو رسائل و جرائد کی آرکائیو", testid: "section-card-journals", accent: "#b05a17", tint: "#f6e3cd" },
  { to: "/ugc-net", icon: GraduationCap, title: "UGC NET اردو", desc: "UGC NET اردو (Paper 1 & Paper 2)", testid: "section-card-ugc", accent: "#14603c", tint: "#d3ecd9" },
];

const VerseBanner = ({ initial }) => {
  const [verse, setVerse] = useState(initial);
  useEffect(() => {
    if (initial) setVerse(initial);
  }, [initial]);
  const next = async () => {
    try {
      const r = await api.get("/verses/random");
      setVerse(r.data);
    } catch {}
  };
  if (!verse) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-14">
      <div className="relative bg-[#e8dfcc] border border-[#c5a059]/60 rounded-2xl px-6 py-10 sm:px-14 text-center shadow-[0_4px_20px_rgba(42,21,8,0.08)]" data-testid="daily-verse-banner">
        <Flourish className="w-40 mx-auto text-[#c5a059]" />
        <h2 className="text-2xl sm:text-3xl font-bold text-[#2a1508] mt-3 leading-[2]">آج کا شعر</h2>
        <Quote size={44} className="absolute top-8 end-8 text-[#d4af37]/50" fill="currentColor" strokeWidth={0} />
        <div className="mt-6 space-y-1">
          <p className="text-2xl sm:text-3xl text-[#2a1508] leading-[2.4]" data-testid="verse-line-1">{verse.line1}</p>
          {verse.line2 && <p className="text-2xl sm:text-3xl text-[#2a1508] leading-[2.4]" data-testid="verse-line-2">{verse.line2}</p>}
        </div>
        <p className="mt-5 text-lg text-[#5c3a21] leading-[2]">
          — {verse.poetSlug ? (
            <Link to={`/authors/${verse.poetSlug}`} data-testid="verse-poet-link" className="hover:text-[#2a1508] underline decoration-[#c5a059] underline-offset-8">{verse.poet}</Link>
          ) : verse.poet}
          {verse.ref ? <span className="text-base"> ، {verse.ref}</span> : null}
        </p>
        <button
          onClick={next}
          data-testid="verse-more-button"
          className="mt-6 inline-flex items-center gap-2 px-7 py-2.5 rounded-full bg-[#2a1508] text-[#f5efe3] hover:bg-[#4a2b16] transition-colors"
        >
          مزید اشعار پڑھیں
          <ArrowLeft size={16} />
        </button>
      </div>
    </section>
  );
};

export default function Home() {
  const [q, setQ] = useState("");
  const [about, setAbout] = useState("");
  const [verse, setVerse] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/home").then((r) => { setAbout(r.data.about); setVerse(r.data.verse); }).catch(() => {});
  }, []);
  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#f5efe3]/60 via-[#f5efe3]/30 to-[#f5efe3]" />
        <div className="relative max-w-4xl mx-auto px-4 py-24 sm:py-32 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#2a1508] leading-[1.7] drop-shadow-sm" data-testid="hero-title">ادب نامہ</h1>
          <p className="mt-3 text-xl sm:text-2xl text-[#4a2b16] leading-[2.2]" data-testid="hero-tagline">اردو ادب کو پڑھیں، سمجھیں، سیکھیں</p>
          <form onSubmit={submit} className="mt-9 mx-auto max-w-xl flex items-center gap-2 bg-white/40 backdrop-blur-md border border-[#c5a059] rounded-full ps-6 pe-2 py-2 shadow-[0_8px_30px_rgba(42,21,8,0.15)]">
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="شاعری، کتاب، ادیب، مضمون تلاش کریں…"
              className="flex-1 bg-transparent outline-none text-[#2a1508] text-lg leading-[2]"
            />
            <button data-testid="hero-search-submit" className="w-11 h-11 rounded-full bg-[#2a1508] text-[#f5efe3] flex items-center justify-center hover:bg-[#4a2b16] transition-colors" aria-label="تلاش">
              <Search size={18} />
            </button>
          </form>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-6" data-testid="sections-grid">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2a1508] leading-[2]">ادب نامہ کے ابواب</h2>
          <Flourish className="w-48 mx-auto mt-3 text-[#c5a059]" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          {SECTIONS.map((s) => (
            <SectionCard key={s.to} {...s} />
          ))}
        </div>
      </section>

      <VerseBanner initial={verse} />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-14">
        <div className="bg-[#fdfbf7] border border-[#c5a059]/40 rounded-2xl p-6 sm:p-10 flex flex-col md:flex-row items-center gap-8 shadow-[0_4px_20px_rgba(42,21,8,0.08)]" data-testid="about-strip">
          <img src={ABOUT_IMG} alt="قدیم کتب اور قلم" className="w-44 h-44 object-cover rounded-xl border border-[#c5a059]/50 shrink-0" />
          <div className="text-right">
            <h3 className="text-2xl sm:text-3xl font-bold text-[#2a1508] leading-[2] flex items-center gap-2">
              <BookOpen size={22} className="text-[#c5a059]" /> ادب نامہ کے بارے میں
            </h3>
            <p className="mt-3 text-lg text-[#5c3a21] leading-[2.3]">{about}</p>
            <Link to="/about" data-testid="about-more-link" className="mt-5 inline-flex items-center gap-2 px-7 py-2.5 rounded-full bg-[#2a1508] text-[#f5efe3] hover:bg-[#4a2b16] transition-colors">
              مزید جانیں <ArrowLeft size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
